import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { SURVEYS_KEY, type AnswerKind } from '../api';

/** ניהול סקרים (PRD 5.10.5). */

export type AdminSurvey = {
  id: string;
  title: string;
  description: string | null;
  is_anonymous: boolean;
  starts_at: string;
  ends_at: string | null;
};

const ADMIN_KEY = ['admin', 'surveys'] as const;

export function useAdminSurveys() {
  return useQuery({
    queryKey: ADMIN_KEY,
    queryFn: async (): Promise<AdminSurvey[]> => {
      const { data, error } = await supabase
        .from('surveys')
        .select('id, title, description, is_anonymous, starts_at, ends_at')
        .order('starts_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as AdminSurvey[];
    },
  });
}

export function useSurveyMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ADMIN_KEY });
    void queryClient.invalidateQueries({ queryKey: SURVEYS_KEY });
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  /**
   * יצירת סקר והשאלות שלו בפעולה אחת (מיגרציה 085).
   *
   * קודם זה היה שתי כתיבות מהדפדפן, והטריגר על ה insert הראשון
   * שלח התראה לכל העובדים לפני שנכנסה שאלה אחת. עכשיו RPC אחד,
   * עסקה אחת, והתראה רק כשיש על מה לענות.
   */
  const save = useMutation({
    mutationFn: async (input: {
      title: string;
      description: string | null;
      is_anonymous: boolean;
      starts_at: string | null;
      ends_at: string | null;
      analysis_note: string | null;
      questions: DraftQuestion[];
    }) => {
      const { error } = await supabase.rpc('save_survey', {
        p_title: input.title,
        p_questions: input.questions.map((question) => ({
          prompt: question.prompt.trim(),
          answer_kind: question.answer_kind,
          options:
            question.answer_kind === 'choice' || question.answer_kind === 'multi'
              ? question.options.map((option) => option.trim()).filter(Boolean)
              : [],
          section: question.section.trim() || null,
          analysis_note: question.analysis_note.trim() || null,
          is_required: question.is_required,
          allow_na: question.answer_kind === 'scale' && question.allow_na,
        })),
        p_description: input.description ?? undefined,
        p_is_anonymous: input.is_anonymous,
        p_starts_at: input.starts_at ?? undefined,
        p_ends_at: input.ends_at ?? undefined,
        p_analysis_note: input.analysis_note ?? undefined,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('surveys').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /**
   * עדכון סקר קיים (מיגרציה 086).
   *
   * השרת הוא שקובע מה נעול. הטופס מציג את הנעילה, אבל הבדיקה
   * האמיתית היא שם — נעילה שמחושבת בדפדפן היא בקשה יפה.
   */
  const update = useMutation({
    mutationFn: async (input: {
      id: string;
      title: string;
      description: string | null;
      is_anonymous: boolean;
      starts_at: string | null;
      ends_at: string | null;
      analysis_note: string | null;
      questions: DraftQuestion[];
    }) => {
      const { error } = await supabase.rpc('update_survey', {
        p_id: input.id,
        p_title: input.title,
        p_questions: input.questions.map((question) => ({
          id: question.id ?? undefined,
          prompt: question.prompt.trim(),
          answer_kind: question.answer_kind,
          options:
            question.answer_kind === 'choice' || question.answer_kind === 'multi'
              ? question.options.map((option) => option.trim()).filter(Boolean)
              : [],
          section: question.section.trim() || null,
          analysis_note: question.analysis_note.trim() || null,
          is_required: question.is_required,
          allow_na: question.answer_kind === 'scale' && question.allow_na,
        })),
        p_description: input.description ?? undefined,
        p_is_anonymous: input.is_anonymous,
        p_starts_at: input.starts_at ?? undefined,
        p_ends_at: input.ends_at ?? undefined,
        p_analysis_note: input.analysis_note ?? undefined,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });
  return { save, update, remove };
}

/**
 * שאלה בטיוטה, לפני שהיא נשמרה.
 *
 * השדות הם מחרוזות ולא null כי זה מה שטופס מחזיק. ההמרה ל null
 * נעשית בשמירה, במקום אחד.
 */
export type DraftQuestion = {
  key: string;
  /** מזהה השאלה בשרת. undefined = שאלה חדשה שעוד לא נשמרה. */
  id?: string;
  prompt: string;
  answer_kind: AnswerKind;
  options: string[];
  section: string;
  analysis_note: string;
  is_required: boolean;
  allow_na: boolean;
};

export function emptyQuestion(section = ''): DraftQuestion {
  return {
    key: crypto.randomUUID(),
    prompt: '',
    answer_kind: 'scale',
    options: [],
    section,
    analysis_note: '',
    is_required: true,
    allow_na: false,
  };
}

/** סקר שנטען לעריכה, כולל מה שהעובד לא רואה (מיגרציה 086). */
export type SurveyForEdit = {
  id: string;
  title: string;
  description: string | null;
  analysis_note: string | null;
  is_anonymous: boolean;
  starts_at: string;
  ends_at: string | null;
  notified_at: string | null;
  /** יש תשובות, ולכן השאלות עצמן נעולות. נקבע בשרת ולא כאן. */
  has_responses: boolean;
  questions: {
    id: string;
    prompt: string;
    answer_kind: AnswerKind;
    options: string[];
    section: string | null;
    analysis_note: string | null;
    is_required: boolean;
    allow_na: boolean;
  }[];
};

export function useSurveyForEdit(surveyId: string | null) {
  return useQuery({
    queryKey: ['admin', 'surveys', 'edit', surveyId],
    enabled: Boolean(surveyId),
    // תמיד טרי: עריכה על בסיס גרסה ישנה היא דריסה שקטה
    staleTime: 0,
    gcTime: 0,
    queryFn: async (): Promise<SurveyForEdit | null> => {
      const { data, error } = await supabase.rpc('survey_for_edit', {
        p_survey: surveyId as string,
      });
      if (error) throw error;
      return (data as unknown as SurveyForEdit | null) ?? null;
    },
  });
}
